import type * as React from "react";

import { registerUserToSlack } from "api/user";
import classNames from "classnames";
import { useAuthenticatedUser } from "hooks/useAuthenticatedUser";
import { useCompany } from "hooks/useCompany";
import { useLogrocketRecording } from "hooks/useLogrocketRecording";
import { useSideNavVisibility } from "hooks/useSideNavVisibility";
import { useEffect, useRef } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useSearchParams } from "react-router";
import { setGainsightUserScope } from "utils/gainsight";
import { setLogrocketUserScope } from "utils/logrocket";

import { DefaultErrorBoundary } from "../ErrorBoundary";
import { PageRouter } from "./components/PageRouter";
import { SideNav } from "./components/SideNav";
import { SystemOverlays } from "./components/SystemOverlays";
import { TopNav } from "./components/TopNav";
import { useStyles } from "./styles";

export const Layout: FC<{ topNav?: React.ReactNode; withoutSideNav?: boolean }> = ({
  className,
  topNav,
  withoutSideNav,
}) => {
  const classes = useStyles();
  const company = useCompany();
  const { csrfTokenInitialized, isLoggedIn, loginDetails, user } = useAuthenticatedUser();
  const [searchParams] = useSearchParams();

  const currentUserId = useRef<string>(undefined);
  const { hasAnyPageAccess } = useSideNavVisibility();
  useLogrocketRecording(Boolean(user && company));

  useEffect(() => {
    if (user && company && user.id !== currentUserId.current) {
      setGainsightUserScope(user, company, loginDetails);
      setLogrocketUserScope(user, company.id);
      currentUserId.current = user.id;
    }
  }, [user, company, loginDetails]);

  useEffect(() => {
    if (isLoggedIn && csrfTokenInitialized && company?.id && user?.email) {
      const slackUserId = searchParams.get("slackUserId");
      const companyIds = searchParams.getAll("companyId");
      const userEmail = searchParams.get("userEmail");
      if (slackUserId) {
        if (companyIds.includes(company?.id) && userEmail?.toLowerCase() === user?.email.toLowerCase()) {
          void registerUserToSlack(slackUserId);
        }
      }
    }
  }, [searchParams, company?.id, user?.email, isLoggedIn, csrfTokenInitialized]);

  return (
    <div className={classNames(classes.columnLayout, className)}>
      {topNav !== undefined ? topNav : <TopNav />}
      <SystemOverlays>
        <div className={classNames(classes.rowLayout, className)}>
          {!withoutSideNav && hasAnyPageAccess && <SideNav className={classes.sideNav} />}

          <ErrorBoundary FallbackComponent={DefaultErrorBoundary}>
            <div className={classes.pages} data-popper-boundary>{user && <PageRouter />}</div>
          </ErrorBoundary>
        </div>
      </SystemOverlays>
    </div>
  );
};
